import { useId, type ReactNode } from "react";
import { Banner, EmptyState, Skeleton } from "./display";

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  numeric?: boolean;
}

export interface DataTableProps<T> {
  caption: string;
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  /** A failed fetch shows an inline retry instead of a blank table (04 §7). */
  error?: boolean;
  onRetry?: () => void;
  errorTitle?: string;
  retryLabel?: string;
  emptyTitle: string;
  emptyAction?: ReactNode;
  sticky?: boolean;
  selectable?: boolean;
  selected?: ReadonlySet<string>;
  onSelectionChange?: (next: Set<string>) => void;
  selectAllLabel?: string;
  selectRowLabel?: (row: T) => string;
  bulkBar?: ReactNode;
}

/**
 * The one table of the product (04 DataTable): real table semantics, loading skeleton, empty state, sticky header, optional
 * row selection with a bulk bar. Person cells use PersonCell (two lines).
 */
/** Arrow up/down moves focus between rows; Home/End jump to the first/last (04 §7, §10). */
function moveBetweenRows(e: React.KeyboardEvent<HTMLTableSectionElement>) {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
  const target = e.target as HTMLElement;
  if (target.closest("input, select, textarea, [role=combobox]")) return;
  const rows = Array.from(e.currentTarget.querySelectorAll<HTMLTableRowElement>(":scope > tr"));
  const at = rows.findIndex((r) => r === target || r.contains(target));
  const to =
    e.key === "Home"
      ? 0
      : e.key === "End"
        ? rows.length - 1
        : Math.max(0, Math.min(rows.length - 1, at + (e.key === "ArrowDown" ? 1 : -1)));
  if (at < 0 || !rows[to]) return;
  e.preventDefault();
  rows[to]!.focus();
}

export function DataTable<T>(p: DataTableProps<T>) {
  const id = useId();
  if (p.error && p.rows === undefined)
    return (
      <Banner
        tone="danger"
        action={
          <button type="button" className="gp-btn" onClick={p.onRetry}>
            {p.retryLabel ?? "Retry"}
          </button>
        }
      >
        {p.errorTitle ?? "Could not load the data."}
      </Banner>
    );
  if (p.loading || p.rows === undefined) return <Skeleton rows={4} />;
  if (p.rows.length === 0) return <EmptyState title={p.emptyTitle} action={p.emptyAction} />;
  const keys = p.rows.map(p.rowKey);
  const sel = p.selected ?? new Set<string>();
  const all = keys.length > 0 && keys.every((k) => sel.has(k));
  const toggle = (k: string, on: boolean) => {
    const next = new Set(sel);
    if (on) next.add(k);
    else next.delete(k);
    p.onSelectionChange?.(next);
  };
  return (
    <div>
      {p.selectable && sel.size > 0 && p.bulkBar ? (
        <div className="gp-bulkbar t-body-strong">{p.bulkBar}</div>
      ) : null}
      <div className="gp-table-wrap">
        <table className={`gp-table${p.sticky ? " gp-table--sticky" : ""}`} aria-labelledby={`${id}-cap`}>
          <caption id={`${id}-cap`} className="gp-sr-only">
            {p.caption}
          </caption>
          <thead>
            <tr>
              {p.selectable ? (
                <th scope="col">
                  <input
                    type="checkbox"
                    aria-label={p.selectAllLabel ?? "Select all"}
                    checked={all}
                    onChange={(e) => p.onSelectionChange?.(e.target.checked ? new Set(keys) : new Set())}
                  />
                </th>
              ) : null}
              {p.columns.map((c) => (
                <th key={c.key} scope="col" className={c.numeric ? "gp-num" : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody onKeyDown={moveBetweenRows}>
            {p.rows.map((row) => {
              const k = p.rowKey(row);
              return (
                <tr key={k} tabIndex={-1} aria-selected={p.selectable ? sel.has(k) : undefined}>
                  {p.selectable ? (
                    <td>
                      <input
                        type="checkbox"
                        aria-label={p.selectRowLabel?.(row) ?? "Select row"}
                        checked={sel.has(k)}
                        onChange={(e) => toggle(k, e.target.checked)}
                      />
                    </td>
                  ) : null}
                  {p.columns.map((c) => (
                    <td key={c.key} className={c.numeric ? "gp-num" : undefined}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
